import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiError } from "@/auth";
import { Check, Loader2, MessageCircle, Send, Mail } from "lucide-react";

const digits = (s) => (s || "").replace(/[^\d]/g, "");
const tgLink = (v) => (/^\+?[\d\s-]+$/.test(v) ? `https://t.me/+${digits(v)}` : `https://t.me/${v.replace(/^@/, "")}`);

export function LicensePurchase({ onDone }) {
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => { axios.get(`${API}/license-plans`).then(r => setPlans(r.data)).catch(() => {}); }, []);

  const confirm = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await axios.post(`${API}/license-orders/purchase`, { plan });
      setDone(data); onDone && onDone();
    } catch (e) { setErr(formatApiError(e)); onDone && onDone(); }
    finally { setBusy(false); }
  };

  if (done) {
    const c = done.contacts || {}; const o = done.order;
    const txt = encodeURIComponent(`Bonjour, je souhaite payer ma licence LHSunlocker ${o.plan} (${o.amount} ${o.currency}). Référence : ${o.reference}`);
    const links = [
      c.admin_whatsapp && { id: "whatsapp", icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/${digits(c.admin_whatsapp)}?text=${txt}` },
      c.admin_telegram && { id: "telegram", icon: Send, label: "Telegram", href: tgLink(c.admin_telegram) },
      c.admin_email && { id: "email", icon: Mail, label: "E-mail", href: `mailto:${c.admin_email}?subject=${encodeURIComponent("Paiement " + o.reference)}&body=${txt}` },
    ].filter(Boolean);
    return (
      <div className="space-y-3" data-testid="purchase-done">
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm text-emerald-400">
          Votre demande a été reçue · référence <b className="font-mono-tech" data-testid="purchase-reference">{o.reference}</b> · {o.plan} · {o.amount} {o.currency}
        </div>
        <p className="text-sm text-zinc-400">L'administrateur vérifie votre paiement puis active votre licence. Contactez-le pour payer :</p>
        <div className="flex flex-wrap gap-2">
          {links.map(l => (
            <a key={l.id} href={l.href} target="_blank" rel="noreferrer" data-testid={`purchase-contact-${l.id}`}
              className="rounded-lg border border-zinc-700 hover:border-orange-500 px-4 py-2 text-sm inline-flex items-center gap-2 transition-colors">
              <l.icon className="w-4 h-4 text-orange-500" /> {l.label}
            </a>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="license-purchase">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {plans.map(p => (
          <button key={p.plan} type="button" onClick={() => setPlan(p.plan)} data-testid={`purchase-plan-${p.plan}`}
            className={`relative rounded-xl border p-5 text-left transition-colors ${plan === p.plan ? "border-orange-500 bg-orange-500/15 ring-2 ring-orange-500 shadow-[0_0_24px_rgba(255,107,0,0.35)]" : "border-zinc-800 bg-[#0C0C10] hover:border-zinc-600"}`}>
            {plan === p.plan && <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-orange-500 text-black inline-flex items-center justify-center"><Check className="w-4 h-4" /></span>}
            <div className="text-xs uppercase tracking-widest text-zinc-400 font-mono-tech">{p.label}</div>
            <div className="font-display text-3xl font-extrabold mt-2">{p.price ?? "—"} <span className="text-base text-zinc-400">{p.currency === "USD" ? "$" : p.currency}</span></div>
            <div className="text-xs text-zinc-500 mt-1">{p.days} jours d'accès</div>
          </button>
        ))}
      </div>
      {err && <div className="text-sm text-red-400" data-testid="purchase-error">{err}</div>}
      <button disabled={!plan || busy} onClick={confirm} data-testid="purchase-confirm"
        className="btn-orange rounded-lg px-6 py-3 text-sm uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-40">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmer ma demande d'achat
      </button>
      <p className="text-xs text-zinc-500">La licence n'est activée qu'après vérification du paiement par l'administrateur.</p>
    </div>
  );
}
